import { Check, Info, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { CARD_GRADIENT_GROUPS, DEFAULT_GRADIENT_ID } from "../../constants/cardGradients";
import type { CardStylesConfig } from "../../types/branding.types";
import { GradientSwatch } from "./GradientSwatch";
import { LogoUploader } from "./LogoUploader";
import { PremiumLockOverlay } from "./PremiumLockOverlay";

export interface CardDesignPanelProps {
  design: CardStylesConfig;
  canCustomize: boolean;
  isSaving: boolean;
  isUploadingLogo: boolean;
  onDraftChange: (draft: CardStylesConfig) => void;
  onSave: (draft: CardStylesConfig) => Promise<void>;
  onReset: () => Promise<void>;
  onUploadLogo: (file: File) => Promise<string>;
}

const RESET_DRAFT: CardStylesConfig = {
  gradient_id: DEFAULT_GRADIENT_ID,
  secondary_logo_url: null,
  secondary_logo_scale: 1,
};

function isDraftEqual(a: CardStylesConfig, b: CardStylesConfig): boolean {
  return (
    (a.gradient_id ?? null) === (b.gradient_id ?? null) &&
    (a.secondary_logo_url ?? null) === (b.secondary_logo_url ?? null) &&
    (a.secondary_logo_scale ?? 1) === (b.secondary_logo_scale ?? 1)
  );
}

function isDraftDefault(draft: CardStylesConfig): boolean {
  return (
    (draft.gradient_id ?? DEFAULT_GRADIENT_ID) === DEFAULT_GRADIENT_ID &&
    !draft.secondary_logo_url &&
    (draft.secondary_logo_scale ?? 1) === 1
  );
}

export function CardDesignPanel({
  design,
  canCustomize,
  isSaving,
  isUploadingLogo,
  onDraftChange,
  onSave,
  onReset,
  onUploadLogo,
}: CardDesignPanelProps) {
  const { t } = useTranslations("cards");

  const [draft, setDraft] = useState<CardStylesConfig>({
    gradient_id: design.gradient_id ?? DEFAULT_GRADIENT_ID,
    secondary_logo_url: design.secondary_logo_url ?? null,
    secondary_logo_scale: design.secondary_logo_scale ?? 1,
  });

  // Logo upload deferred to Save — avoids orphaning S3 objects if the user
  // picks a file then bails. Preview uses a local blob URL until then.
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const setLocalLogoPreview = (file: File) => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    const blobUrl = URL.createObjectURL(file);
    blobUrlRef.current = blobUrl;
    setPendingLogoFile(file);
    update({ secondary_logo_url: blobUrl });
  };

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const update = (patch: CardStylesConfig) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    onDraftChange(next);
  };

  const handleFile = (file: File) => {
    setLocalLogoPreview(file);
  };

  const handleClear = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPendingLogoFile(null);
    update({ secondary_logo_url: null });
  };

  const handleSave = async () => {
    try {
      let finalDraft = draft;

      if (pendingLogoFile) {
        const uploadedUrl = await onUploadLogo(pendingLogoFile);
        finalDraft = { ...draft, secondary_logo_url: uploadedUrl };
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        setPendingLogoFile(null);
        setDraft(finalDraft);
        onDraftChange(finalDraft);
      }

      await onSave(finalDraft);
      // Iframe CSS has a 60s HTTP cache — open cards won't update instantly.
      colocaToast.success(t("branding_saved_with_delay"), { duration: 6000 });
    } catch (err) {
      colocaToast.error(err instanceof Error ? err.message : t("branding_save_failed"));
    }
  };

  const handleReset = async () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPendingLogoFile(null);
    setDraft(RESET_DRAFT);
    onDraftChange(RESET_DRAFT);
    try {
      await onReset();
      colocaToast.success(t("branding_reset_done"));
    } catch (err) {
      colocaToast.error(err instanceof Error ? err.message : t("branding_reset_failed"));
    }
  };

  const dirty = !isDraftEqual(draft, design);
  const isAtDefault = isDraftDefault(draft);

  return (
    <div className="relative">
      {!canCustomize && <PremiumLockOverlay />}

      <div
        className={cn("space-y-4", !canCustomize && "pointer-events-none select-none")}
        aria-hidden={!canCustomize}
      >
        <div className="flex items-start gap-3 rounded-lg border border-warning-container bg-warning-container/30 p-4 text-sm">
          <Info className="mt-0.5 size-4 shrink-0 text-warning-container-foreground" aria-hidden="true" />
          <div className="min-w-0">
            <div className="font-medium text-foreground">{t("branding_propagation_notice_title")}</div>
            <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t("branding_propagation_notice_body")}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-foreground">{t("branding_gradient_section_title")}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t("branding_gradient_section_description")}
              </div>
            </div>
          </div>
          <div className="space-y-5">
            {CARD_GRADIENT_GROUPS.map((group) => (
              <div key={group.id}>
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {group.name}
                </div>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                  {group.items.map((g) => (
                    <GradientSwatch
                      key={g.id}
                      gradient={g}
                      selected={(draft.gradient_id ?? DEFAULT_GRADIENT_ID) === g.id}
                      disabled={!canCustomize}
                      onClick={() => update({ gradient_id: g.id })}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-foreground">{t("branding_logo_section_title")}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t("branding_logo_section_description")}
              </div>
            </div>
          </div>
          <LogoUploader
            value={draft.secondary_logo_url ?? null}
            onFile={handleFile}
            onClear={handleClear}
            disabled={!canCustomize}
            isUploading={isUploadingLogo}
          />
          {draft.secondary_logo_url && (
            <div className="mt-4 border-t border-border pt-4">
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="branding-logo-scale" className="text-sm font-medium text-foreground">
                  {t("branding_logo_scale_label")}
                </label>
                <span className="font-mono text-xs text-muted-foreground">
                  {Math.round((draft.secondary_logo_scale ?? 1) * 100)}%
                </span>
              </div>
              <input
                id="branding-logo-scale"
                type="range"
                min={0.5}
                max={1.8}
                step={0.05}
                value={draft.secondary_logo_scale ?? 1}
                onChange={(e) =>
                  update({
                    secondary_logo_scale: parseFloat(e.target.value),
                  })
                }
                disabled={!canCustomize}
                className="w-full accent-primary"
              />
              <div className="mt-0.5 flex justify-between text-[10px] text-muted-foreground">
                <span>50%</span>
                <span>100%</span>
                <span>180%</span>
              </div>
            </div>
          )}
        </div>

        {/* The note's 12rem basis is what lets the row wrap: on a narrow panel the buttons drop to their own
            line instead of pushing the footer past the card's edge. */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted p-4">
          <div className="min-w-0 flex-[1_1_12rem] text-xs text-muted-foreground">
            {isAtDefault ? t("branding_footer_default") : t("branding_footer_applies_to_all")}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ColocaButton
              variant="ghost"
              size="sm"
              icon={RotateCcw}
              disabled={isAtDefault || isSaving || !canCustomize}
              onClick={handleReset}
            >
              {t("branding_button_reset")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              size="sm"
              icon={Check}
              loading={isSaving}
              disabled={!dirty || !canCustomize}
              onClick={handleSave}
            >
              {t("branding_button_save")}
            </ColocaButton>
          </div>
        </div>
      </div>
    </div>
  );
}
